import { createSlice } from "@reduxjs/toolkit";
import {
  clearAuthStorage,
  markLoggedOut,
  markSession,
  shouldRestoreSession,
} from "@/features/auth/authStorage";
import { extractPermissions } from "@/features/auth/permissions";
import { usesLocalJwtAuth } from "@/services/api/baseUrl";

function initialAuthStatus() {
  if (usesLocalJwtAuth() || shouldRestoreSession()) {
    return "loading";
  }
  return "idle";
}

/**
 * `status` drives the route guards:
 *   idle            — nothing attempted yet
 *   loading         — cold start only: a session probably exists (storage hint)
 *                     and /me has not answered yet
 *   authenticated   — user loaded
 *   unauthenticated — no session; RequireAuth may redirect to login
 *
 * The old app inferred this from a decrypted localStorage user, which meant a
 * stale cached user could render protected pages before the server disagreed.
 */
const initialState = {
  user: null,
  permissions: [],
  status: initialAuthStatus(),
  // Path that received a 403 from the server, so ProtectedRoute can show the
  // inline denied view for that path only rather than trusting local permissions.
  permissionDeniedPath: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Only a cold start may show a loading state. Once a user is on screen, a
     * revalidation must never move the status back to `loading` — that is what
     * used to flash the full-screen "Restoring your session" spinner on every
     * navigation, and it is the invariant that keeps it from coming back.
     */
    authRequestStarted: (state) => {
      if (state.status !== "authenticated") {
        state.status = "loading";
      }
    },

    setCredentials: (state, action) => {
      const user = action.payload?.user ?? null;
      state.user = user;
      state.permissions = extractPermissions(user);
      state.status = user ? "authenticated" : "unauthenticated";
      state.permissionDeniedPath = null;
      if (user) markSession();
    },

    setPermissionDeniedPath: (state, action) => {
      state.permissionDeniedPath = action.payload;
    },

    authFailed: (state) => {
      state.user = null;
      state.permissions = [];
      state.status = "unauthenticated";
    },

    logOut: (state) => {
      state.user = null;
      state.permissions = [];
      state.status = "unauthenticated";
      state.permissionDeniedPath = null;
      clearAuthStorage();
      markLoggedOut();
    },
  },
});

export const { authRequestStarted, setCredentials, setPermissionDeniedPath, authFailed, logOut } =
  authSlice.actions;

export default authSlice.reducer;

export const selectCurrentUser = (state) => state.auth.user;
export const selectPermissions = (state) => state.auth.permissions;
export const selectAuthStatus = (state) => state.auth.status;
export const selectPermissionDeniedPath = (state) => state.auth.permissionDeniedPath;
export const selectIsAuthResolved = (state) =>
  state.auth.status === "authenticated" || state.auth.status === "unauthenticated";
